// src/components/plan/TeachingPlanEditor.js
// The Step 7 teaching plan as an editable table: edit any cell, add, remove and reorder rows.
// On small screens each row becomes a card. Changes autosave like every other step.
import React from "react";
import {
  Box, Button, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { PLAN_COLUMNS, emptyRow, mluCodeOf } from "../../utils/teachingPlan";
import { FW_FG } from "./frameworkColors";

// The course structure is the densest column, so it gets most of the width.
export const COL_WIDTHS = { session: "10%", mlu: "17%", methods: "20%", structure: undefined };

function RowActions({ index, count, onMove, onRemove }) {
  return (
    <Stack direction="row" spacing={0} justifyContent="flex-end">
      <Tooltip title="Move up"><span>
        <IconButton size="small" aria-label={`Move row ${index + 1} up`} disabled={index === 0} onClick={() => onMove(index, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
      </span></Tooltip>
      <Tooltip title="Move down"><span>
        <IconButton size="small" aria-label={`Move row ${index + 1} down`} disabled={index === count - 1} onClick={() => onMove(index, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
      </span></Tooltip>
      <Tooltip title="Remove row">
        <IconButton size="small" aria-label={`Remove row ${index + 1}`} onClick={() => onRemove(index)}><DeleteOutlineIcon fontSize="small" /></IconButton>
      </Tooltip>
    </Stack>
  );
}

export default function TeachingPlanEditor({ plan, onChange, disabled }) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("md"));
  const rows = plan?.rows || [];

  const update = (patch) => onChange({ ...plan, ...patch });
  const setCell = (i, key, value) => update({ rows: rows.map((r, j) => (j === i ? { ...r, [key]: value } : r)) });
  const move = (i, d) => {
    const next = [...rows];
    const [r] = next.splice(i, 1);
    next.splice(i + d, 0, r);
    update({ rows: next });
  };
  const remove = (i) => update({ rows: rows.filter((_, j) => j !== i) });
  const add = () => update({ rows: [...rows, { ...emptyRow(), session: `Session ${rows.length + 1}` }] });

  const cellField = (i, col, extra = {}) => (
    <TextField
      value={rows[i][col.key] || ""}
      onChange={(e) => setCell(i, col.key, e.target.value)}
      multiline
      minRows={col.key === "structure" ? 4 : 1}
      fullWidth
      size="small"
      variant="standard"
      disabled={disabled}
      inputProps={{ "aria-label": `${col.label}, row ${i + 1}`, maxLength: col.key === "session" ? 200 : 2000 }}
      InputProps={{ disableUnderline: false, sx: { fontSize: "0.875rem", lineHeight: 1.5 } }}
      {...extra}
    />
  );

  return (
    <Box>
      <TextField
        label="Introduction"
        value={plan?.intro || ""}
        onChange={(e) => update({ intro: e.target.value })}
        multiline
        minRows={2}
        fullWidth
        size="small"
        disabled={disabled}
        sx={{ mb: 2 }}
        inputProps={{ maxLength: 2000 }}
      />

      {compact ? (
        <Stack spacing={1.5}>
          {rows.map((r, i) => {
            const fw = mluCodeOf(r.mlu)?.slice(0, 3);
            return (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, borderLeft: "4px solid", borderLeftColor: FW_FG[fw] || "grey.400" }}>
                <Stack spacing={1}>
                  {PLAN_COLUMNS.map((c) => (
                    <Box key={c.key}>
                      <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                      {cellField(i, c)}
                    </Box>
                  ))}
                  <RowActions index={i} count={rows.length} onMove={move} onRemove={remove} />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      ) : (
        <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Table size="small" aria-label="Editable teaching plan" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                {PLAN_COLUMNS.map((c) => (
                  <TableCell key={c.key} sx={{ width: COL_WIDTHS[c.key], fontWeight: 600 }}>{c.label}</TableCell>
                ))}
                <TableCell sx={{ width: 112 }} aria-label="Row actions" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => {
                const fw = mluCodeOf(r.mlu)?.slice(0, 3);
                return (
                  <TableRow key={i} sx={{ verticalAlign: "top", "&:nth-of-type(even)": { bgcolor: "grey.50" } }}>
                    {PLAN_COLUMNS.map((c, ci) => (
                      <TableCell key={c.key} sx={ci === 0 ? { borderLeft: "4px solid", borderLeftColor: FW_FG[fw] || "transparent" } : undefined}>
                        {cellField(i, c)}
                      </TableCell>
                    ))}
                    <TableCell><RowActions index={i} count={rows.length} onMove={move} onRemove={remove} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Button startIcon={<AddIcon />} onClick={add} disabled={disabled || rows.length >= 60} sx={{ mt: 1.5 }}>Add a session</Button>

      <TextField
        label="Closing notes (optional)"
        value={plan?.outro || ""}
        onChange={(e) => update({ outro: e.target.value })}
        multiline
        minRows={1}
        fullWidth
        size="small"
        disabled={disabled}
        sx={{ mt: 2 }}
        inputProps={{ maxLength: 2000 }}
      />
    </Box>
  );
}
